import { useEffect, useMemo, useRef, useState } from 'react';
import type { EventClient } from '@aicores/events';
import type { PortalContent, Prompt } from './types';
import { IconArrowRight, IconCheck, IconClose } from './icons';
import {
  PILLAR_LABELS,
  SECTIONS,
  computeResult,
  interpretationText,
  loadResult,
  saveResult,
  type PFOption,
  type PFSection,
  type PillarId,
  type PriorityFinderResult,
  type SectionScore,
} from './priority-finder-data';

interface Props {
  open: boolean;
  bonusId: string;
  productId: string;
  content: PortalContent;
  events: EventClient;
  onClose: () => void;
  onCompleted: () => void;
  previousResult: PriorityFinderResult | null;
  copiedPromptIds: Set<string>;
}

type Screen = 'intro' | 'questions' | 'results';

export function PriorityFinder({
  open,
  bonusId,
  productId,
  content,
  events,
  onClose,
  onCompleted,
  previousResult,
  copiedPromptIds,
}: Props) {
  const [screen, setScreen] = useState<Screen>('intro');
  const [sectionIndex, setSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<PriorityFinderResult | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    overlayRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [sectionIndex, screen]);

  useEffect(() => {
    if (!open) return;
    // Reset state each time the modal is opened.
    setScreen('intro');
    setSectionIndex(0);
    setAnswers({});
    setResult(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  function startAssessment() {
    events.fire('assessment_started', { bonus_id: bonusId });
    setScreen('questions');
    setSectionIndex(0);
  }

  function viewSavedResults() {
    if (!previousResult) return;
    setResult(previousResult);
    setScreen('results');
  }

  const currentSection = SECTIONS[sectionIndex];
  const currentAnswered =
    !!currentSection && currentSection.questions.every((q) => answers[q.id] !== undefined);

  function next() {
    if (!currentSection || !currentAnswered) return;
    if (sectionIndex < SECTIONS.length - 1) {
      setSectionIndex(sectionIndex + 1);
    } else {
      const r = computeResult(answers);
      setResult(r);
      saveResult(productId, bonusId, r);
      events.fire('assessment_completed', {
        bonus_id: bonusId,
        overall_score: r.overall_score,
        overall_pct: r.overall_pct,
        pillar_scores: Object.fromEntries(
          r.section_scores.map((s) => [s.id, { points: s.points, pct: s.pct }]),
        ),
        implementation_order: r.implementation_order,
        answers: r.answers,
        is_retake: previousResult !== null,
      });
      onCompleted();
      setScreen('results');
    }
  }

  function back() {
    if (sectionIndex > 0) setSectionIndex(sectionIndex - 1);
    else setScreen('intro');
  }

  return (
    <div
      className="pf-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Priority Finder"
      ref={overlayRef}
    >
      <div className="pf-container">
        <button type="button" className="pf-close" aria-label="Close" onClick={onClose}>
          <IconClose />
        </button>
        {screen === 'intro' ? (
          <IntroScreen
            previousResult={previousResult}
            onStart={startAssessment}
            onViewResults={viewSavedResults}
          />
        ) : screen === 'questions' && currentSection ? (
          <QuestionsScreen
            section={currentSection}
            sectionIndex={sectionIndex}
            answers={answers}
            onAnswer={(questionId, points) =>
              setAnswers((prev) => ({ ...prev, [questionId]: points }))
            }
            onBack={back}
            onNext={next}
            canProceed={currentAnswered}
            isLast={sectionIndex === SECTIONS.length - 1}
          />
        ) : result ? (
          <ResultsScreen
            result={result}
            previousResult={result === previousResult ? null : previousResult}
            content={content}
            copiedPromptIds={copiedPromptIds}
            onRetake={() => {
              setScreen('intro');
              setSectionIndex(0);
              setAnswers({});
              setResult(null);
            }}
            onClose={onClose}
          />
        ) : null}
      </div>
    </div>
  );
}

function IntroScreen({
  previousResult,
  onStart,
  onViewResults,
}: {
  previousResult: PriorityFinderResult | null;
  onStart: () => void;
  onViewResults: () => void;
}) {
  return (
    <div className="pf-screen pf-intro">
      <div className="pf-eyebrow">Bonus 01 · Priority Finder</div>
      <h1 className="pf-title">Score your business across the five CORES® pillars.</h1>
      <p className="pf-subtitle">
        Answer 16 questions honestly. There are no right answers, only accurate ones. At the end,
        you will have a colour-coded Priority Matrix and a personalised implementation order for the
        12 systems. About 10 minutes.
      </p>
      {previousResult ? (
        <div className="pf-prev">
          <div className="pf-prev-label">Your last result</div>
          <div className="pf-prev-row">
            <div>
              <div className="pf-prev-score">
                {previousResult.overall_pct}%
                <span className={`pf-pct-dot is-${previousResult.overall_rating}`} />
              </div>
              <div className="pf-prev-date">
                Completed {new Date(previousResult.completed_at).toLocaleDateString()}
              </div>
            </div>
            <div className="pf-prev-pillars">
              {previousResult.section_scores.map((s) => (
                <span key={s.id} className={`pf-pill is-${s.rating}`}>
                  {s.id} {s.pct}%
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      <div className="pf-intro-actions">
        {previousResult ? (
          <>
            <button type="button" className="pf-primary-btn" onClick={onViewResults}>
              View my results
              <IconArrowRight aria-hidden="true" />
            </button>
            <button type="button" className="pf-secondary-btn" onClick={onStart}>
              Retake the assessment
            </button>
          </>
        ) : (
          <button type="button" className="pf-primary-btn" onClick={onStart}>
            Start assessment
            <IconArrowRight aria-hidden="true" />
          </button>
        )}
      </div>
      <p className="pf-footnote">
        Your answers and results stay in this browser. Your score and implementation order are logged
        to your AICORES® account so you can compare over time.
      </p>
    </div>
  );
}

function QuestionsScreen({
  section,
  sectionIndex,
  answers,
  onAnswer,
  onBack,
  onNext,
  canProceed,
  isLast,
}: {
  section: PFSection;
  sectionIndex: number;
  answers: Record<string, number>;
  onAnswer: (questionId: string, points: number) => void;
  onBack: () => void;
  onNext: () => void;
  canProceed: boolean;
  isLast: boolean;
}) {
  const totalSections = SECTIONS.length;
  const progressPct = ((sectionIndex + 1) / totalSections) * 100;

  return (
    <div className="pf-screen pf-questions">
      <div className="pf-progress">
        <div className="pf-progress-bar">
          <div className="pf-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="pf-progress-meta">
          Section {sectionIndex + 1} of {totalSections} · {section.letter} · {section.name}
        </div>
      </div>
      <h2 className="pf-section-title">
        <span className="pf-section-letter">{section.letter}</span>
        {section.name}
      </h2>
      <p className="pf-section-tagline">{section.tagline}</p>

      <div className="pf-questions-list">
        {section.questions.map((q, qi) => (
          <div key={q.id} className="pf-question">
            <div className="pf-question-num">Q{questionOffset(sectionIndex, qi) + 1}</div>
            <div className="pf-question-text">{q.text}</div>
            <div className="pf-options">
              {q.options.map((opt) => (
                <OptionCard
                  key={opt.text}
                  option={opt}
                  selected={answers[q.id] === opt.points}
                  onSelect={() => onAnswer(q.id, opt.points)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="pf-nav">
        <button type="button" className="pf-secondary-btn" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="pf-primary-btn"
          onClick={onNext}
          disabled={!canProceed}
        >
          {isLast ? 'See my results' : 'Next section'}
          <IconArrowRight aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function OptionCard({
  option,
  selected,
  onSelect,
}: {
  option: PFOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`pf-option is-${option.color}${selected ? ' is-selected' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className={`pf-option-dot is-${option.color}`} aria-hidden="true" />
      <span className="pf-option-text">{option.text}</span>
      {selected ? <IconCheck className="pf-option-check" aria-hidden="true" /> : null}
    </button>
  );
}

function questionOffset(sectionIndex: number, qi: number): number {
  let offset = 0;
  for (let i = 0; i < sectionIndex; i++) {
    const section = SECTIONS[i];
    if (section) offset += section.questions.length;
  }
  return offset + qi;
}

function ResultsScreen({
  result,
  previousResult,
  content,
  copiedPromptIds,
  onRetake,
  onClose,
}: {
  result: PriorityFinderResult;
  previousResult: PriorityFinderResult | null;
  content: PortalContent;
  copiedPromptIds: Set<string>;
  onRetake: () => void;
  onClose: () => void;
}) {
  const weakestPillar = useMemo(
    () => [...result.section_scores].sort((a, b) => a.pct - b.pct)[0],
    [result],
  );

  const promptLookup = useMemo(() => {
    const map = new Map<string, Prompt>();
    for (const p of content.prompts) map.set(p.prompt_id, p);
    return map;
  }, [content.prompts]);

  function scrollToPrompt(promptId: string) {
    onClose();
    // onClose unlocks scroll; wait a frame for the modal to unmount before scrolling.
    window.setTimeout(() => {
      const el = document.getElementById(`prompt-${promptId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        const header = el.querySelector<HTMLButtonElement>('.prompt-header');
        if (!el.classList.contains('is-expanded')) header?.click();
      }, 350);
    }, 50);
  }

  return (
    <div className="pf-screen pf-results">
      <div className="pf-eyebrow">Your Priority Matrix</div>
      <div className="pf-overall">
        <div className={`pf-overall-score is-${result.overall_rating}`}>
          {result.overall_pct}%
        </div>
        <div className="pf-overall-label">
          Overall CORES® score
          {previousResult ? (
            <ScoreDelta current={result.overall_pct} previous={previousResult.overall_pct} />
          ) : null}
        </div>
      </div>

      <p className="pf-interpretation">{interpretationText(result.overall_pct)}</p>

      <div className="pf-pillar-grid">
        {result.section_scores.map((section) => (
          <PillarCard
            key={section.id}
            section={section}
            isWeakest={weakestPillar?.id === section.id}
            previous={
              previousResult?.section_scores.find((s) => s.id === section.id) ?? null
            }
          />
        ))}
      </div>

      <div className="pf-impl-order">
        <div className="pf-impl-order-header">
          <h3>Implementation order</h3>
          <p>
            The 12 systems sorted so you tackle the weakest pillar first. Start with the top three.
          </p>
        </div>
        <ol className="pf-impl-list">
          {result.implementation_order.map((promptId, idx) => {
            const prompt = promptLookup.get(promptId);
            if (!prompt) return null;
            const isFirstPriority = idx < 3;
            const isCopied = copiedPromptIds.has(promptId);
            const classes = ['pf-impl-item'];
            if (isFirstPriority) classes.push('is-first-priority');
            if (isCopied) classes.push('is-done');
            return (
              <li key={promptId} className={classes.join(' ')}>
                <div className="pf-impl-rank">
                  {isCopied ? (
                    <span className="pf-impl-rank-check" aria-label="Copied">
                      <IconCheck />
                    </span>
                  ) : (
                    String(idx + 1).padStart(2, '0')
                  )}
                </div>
                <div className="pf-impl-body">
                  <div className="pf-impl-title">
                    {prompt.title}
                    {isFirstPriority ? <span className="pf-first-priority">First priority</span> : null}
                    {isCopied ? <span className="pf-done-badge">Copied</span> : null}
                  </div>
                  {prompt.what_it_does ? (
                    <div className="pf-impl-desc">{prompt.what_it_does}</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="pf-impl-cta"
                  onClick={() => scrollToPrompt(promptId)}
                >
                  {isCopied ? 'Open again' : 'Take me there'}
                  <IconArrowRight aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="pf-nav">
        <button type="button" className="pf-secondary-btn" onClick={onRetake}>
          Retake
        </button>
        <button type="button" className="pf-primary-btn" onClick={onClose}>
          Save &amp; return to the portal
          <IconArrowRight aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function PillarCard({
  section,
  isWeakest,
  previous,
}: {
  section: SectionScore;
  isWeakest: boolean;
  previous: SectionScore | null;
}) {
  return (
    <div className={`pf-pillar-card is-${section.rating}${isWeakest ? ' is-weakest' : ''}`}>
      {isWeakest ? <div className="pf-pillar-badge">Start here</div> : null}
      <div className="pf-pillar-letter">{section.id}</div>
      <div className="pf-pillar-name">{PILLAR_LABELS[section.id as PillarId]}</div>
      <div className="pf-pillar-score">
        {section.pct}%
        {previous ? <ScoreDelta current={section.pct} previous={previous.pct} /> : null}
      </div>
      <div className="pf-pillar-points">
        {section.points} of {section.maxPoints} points
      </div>
    </div>
  );
}

function ScoreDelta({ current, previous }: { current: number; previous: number }) {
  const delta = current - previous;
  if (delta === 0) {
    return (
      <span className="pf-delta is-flat" aria-label={`No change from previous ${previous}%`}>
        → 0
      </span>
    );
  }
  if (delta > 0) {
    return (
      <span className="pf-delta is-up" aria-label={`Up ${delta} points from previous ${previous}%`}>
        ↑ {delta}
      </span>
    );
  }
  return (
    <span
      className="pf-delta is-down"
      aria-label={`Down ${Math.abs(delta)} points from previous ${previous}%`}
    >
      ↓ {Math.abs(delta)}
    </span>
  );
}
